import React, { useState, useEffect } from "react";
import SEO from "../components/SEO";

const translations = {
  tr: {
    backToArticles: "Makalelere Dön",
    category: "Kategori",
    date: "Tarih",
    relatedArticles: "İlgili Makaleler",
    readMore: "Devamını Oku",
    share: "Paylaş",
    loading: "Yükleniyor...",
    notFound: "Makale bulunamadı",
    error: "Makale yüklenirken bir hata oluştu"
  },
  en: {
    backToArticles: "Back to Articles",
    category: "Category",
    date: "Date",
    relatedArticles: "Related Articles",
    readMore: "Read More",
    share: "Share",
    loading: "Loading...",
    notFound: "Article not found",
    error: "Error loading article"
  },
  fr: {
    backToArticles: "Retour aux Articles",
    category: "Catégorie",
    date: "Date",
    relatedArticles: "Articles Connexes",
    readMore: "Lire la Suite",
    share: "Partager",
    loading: "Chargement...",
    notFound: "Article introuvable",
    error: "Erreur lors du chargement"
  }
};

const API_URL = 'https://x-consulting-backend.onrender.com';

const detailStyles = `
  .ap-row-centered.row {
    display: flex !important;
    justify-content: center !important;
  }

  .ap-content-block,
  .ap-content-block p,
  .ap-content-block div,
  .ap-content-block span,
  .ap-content-block li,
  .ap-content-block h1,
  .ap-content-block h2,
  .ap-content-block h3,
  .ap-content-block h4,
  .ap-content-block h5,
  .ap-content-block h6 {
    text-align: center !important;
  }
  .ap-content-block ul,
  .ap-content-block ol {
    list-style-position: inside;
    padding-left: 0;
    text-align: center;
  }

  .ap-related-grid.row {
    display: flex !important;
    flex-wrap: wrap !important;
    justify-content: center !important;
  }

  @media (max-width: 991px) {
    .ap-related-grid.row {
      flex-direction: column !important;
      align-items: center !important;
    }
    .ap-related-grid .ap-related-col {
      width: 90% !important;
      max-width: 500px !important;
      float: none !important;
      padding-left: 0 !important;
      padding-right: 0 !important;
    }
  }
`;

export const ArticlePage = ({ lang, isDarkMode, articleId, onBackClick, onArticleClick }) => {
  const [article, setArticle] = useState(null);
  const [relatedArticles, setRelatedArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const currentLang = lang ? lang.toUpperCase() : 'TR';
  const t = translations[lang?.toLowerCase()] || translations.tr;

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchArticle();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [articleId]);

  const fetchArticle = async () => {
    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${API_URL}/api/article/${articleId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        }
      });

      if (response.ok) {
        const data = await response.json();
        setArticle(data);

        if (data.category) {
          fetchRelatedArticles(data.category, data.id);
        }
      } else {
        setError(t.notFound);
      }
    } catch (err) {
      setError(t.error);
      console.error('Fetch article error:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchRelatedArticles = async (category, currentArticleId) => {
    try {
      const response = await fetch(`${API_URL}/api/article/category/${category}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        }
      });

      if (response.ok) {
        const data = await response.json();
        const related = data
          .filter(a => a.id !== currentArticleId)
          .slice(0, 3);
        setRelatedArticles(related);
      }
    } catch (err) {
      console.error('Related articles fetch error:', err);
    }
  };

  const handleBackClick = () => {
    if (onBackClick) {
      onBackClick();
    }
  };

  const handleRelatedArticleClick = (relatedArticleId) => {
    if (onArticleClick) {
      onArticleClick(relatedArticleId);
    }
  };

  const handleShare = (platform) => {
    const url = window.location.href;
    const shareTitle = getLocalizedField(article, 'title');

    let shareUrl = '';
    switch (platform) {
      case 'twitter':
        shareUrl = `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(shareTitle)}`;
        break;
      case 'facebook':
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`;
        break;
      case 'linkedin':
        shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`;
        break;
      default:
        return;
    }

    window.open(shareUrl, '_blank', 'width=600,height=400');
  };

  const getLocalizedField = (article, field) => {
    if (!article) return '';
    const langField = `${field}${currentLang}`;
    const fallbackField = `${field}TR`;
    return article[langField] || article[fallbackField] || '';
  };

  const shareBtnStyle = {
    color: '#fff',
    border: 'none',
    width: '45px',
    height: '45px',
    borderRadius: '50%',
    cursor: 'pointer',
    marginRight: '10px',
    fontSize: '18px',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.3s ease'
  };

  // Loading state
  if (loading) {
    return (
      <div style={{ paddingTop: '130px', textAlign: 'center', minHeight: '60vh' }}>
        <i className="fa fa-spinner fa-spin" style={{ fontSize: '48px', color: '#1e7a46' }}></i>
        <h2 style={{ marginTop: '20px', color: '#666' }}>{t.loading}</h2>
      </div>
    );
  }

  // Error state
  if (error || !article) {
    return (
      <div style={{ paddingTop: '130px', textAlign: 'center', minHeight: '60vh' }}>
        <i className="fa fa-exclamation-circle" style={{ fontSize: '48px', color: '#dc3545' }}></i>
        <h2 style={{ marginTop: '20px', color: '#666' }}>{error || t.notFound}</h2>
        <button
          onClick={handleBackClick}
          style={{
            marginTop: '20px',
            padding: '12px 30px',
            background: '#1e7a46',
            color: '#fff',
            border: 'none',
            borderRadius: '25px',
            cursor: 'pointer',
            fontSize: '16px'
          }}
        >
          <i className="fa fa-arrow-left"></i> {t.backToArticles}
        </button>
      </div>
    );
  }

  const title = getLocalizedField(article, 'title');
  const subtitle = getLocalizedField(article, 'subtitle');
  const description = getLocalizedField(article, 'description');
  const content = getLocalizedField(article, 'content');

  return (
    <div className="article-page" style={{ paddingTop: '120px', paddingBottom: '40px' }}>
      <style>{detailStyles}</style>

      <SEO
        title={title}
        description={description}
        keywords={`${title}, ${article.category || 'hukuk'}, hukuk, içtihat, yargıtay`}
        url={`/article/${article.id}`}
        lang={currentLang.toLowerCase()}
        image={article.image || 'img/portfolio/01-small.jpg'}
        type="article"
      />

      {/* Makale Header */}
      <div className="article-header" style={{ marginBottom: '30px' }}>
        <div className="container">
          <div className="row ap-row-centered">
            <div className="col-md-10">
              <div className="article-meta" style={{ marginBottom: '20px', textAlign: 'center', display: 'flex', justifyContent: 'center', flexWrap: 'wrap', gap: '15px' }}>
                {article.category && (
                  <span style={{
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: '#fff',
                    padding: '6px 15px',
                    borderRadius: '20px',
                    fontSize: '14px'
                  }}>
                    {article.category}
                  </span>
                )}
                <span style={{ color: isDarkMode ? '#999' : '#666' }}>
                  <i className="fa fa-calendar"></i> {article.date}
                </span>
                {article.readTime && (
                  <span style={{ color: isDarkMode ? '#999' : '#666' }}>
                    <i className="fa fa-clock-o"></i> {article.readTime}
                  </span>
                )}
              </div>
              <h1 style={{
                fontSize: '42px',
                marginBottom: '15px',
                fontWeight: '700',
                textAlign: 'center',
                color: isDarkMode ? '#f0f0f0' : '#333'
              }}>
                {title}
              </h1>
              {subtitle && (
                <h2 style={{
                  fontSize: '24px',
                  color: isDarkMode ? '#bbb' : '#666',
                  marginBottom: '20px',
                  textAlign: 'center'
                }}>
                  {subtitle}
                </h2>
              )}
              {article.author && (
                <div style={{ color: isDarkMode ? '#999' : '#999', fontSize: '16px', textAlign: 'center' }}>
                  <i className="fa fa-user"></i> {article.author}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Makale Görseli */}
      {article.image && (
        <div style={{ marginBottom: '30px' }}>
          <div className="container">
            <div className="row ap-row-centered">
              <div className="col-md-10">
                <img
                  src={article.image}
                  alt={title}
                  style={{
                    width: '100%',
                    height: 'auto',
                    borderRadius: '10px',
                    boxShadow: '0 10px 30px rgba(0,0,0,0.1)'
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Makale İçeriği */}
      <div style={{ marginBottom: '30px' }}>
        <div className="container">
          <div className="row ap-row-centered">
            <div className="col-md-8">
              <div
                className="article-content ap-content-block"
                style={{
                  fontSize: '18px',
                  lineHeight: '1.8',
                  color: isDarkMode ? '#ccc' : '#444'
                }}
                dangerouslySetInnerHTML={{ __html: content }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Paylaş Butonları - Tek, Ortalanmış */}
      <div style={{ textAlign: 'center', padding: '30px 0', borderTop: isDarkMode ? '1px solid #333' : '1px solid #eee' }}>
        <span style={{ fontWeight: '600', color: isDarkMode ? '#bbb' : '#666', marginRight: '15px', fontSize: '16px' }}>{t.share}:</span>
        <button
          onClick={() => handleShare('twitter')}
          style={{ ...shareBtnStyle, background: '#1DA1F2' }}
        >
          <i className="fa fa-twitter"></i>
        </button>
        <button
          onClick={() => handleShare('facebook')}
          style={{ ...shareBtnStyle, background: '#4267B2' }}
        >
          <i className="fa fa-facebook"></i>
        </button>
        <button
          onClick={() => handleShare('linkedin')}
          style={{ ...shareBtnStyle, background: '#0077B5', marginRight: 0 }}
        >
          <i className="fa fa-linkedin"></i>
        </button>
      </div>

      {/* Makalelere Dön Butonu - En Alt, Ortalanmış */}
      <div style={{ textAlign: 'center', paddingTop: '20px', paddingBottom: '20px' }}>
        <button
          onClick={handleBackClick}
          style={{
            background: 'transparent',
            border: '2px solid #1e7a46',
            padding: '14px 36px',
            borderRadius: '25px',
            cursor: 'pointer',
            color: '#1e7a46',
            fontSize: '16px',
            fontWeight: '600',
            transition: 'all 0.3s ease',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '10px'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = '#1e7a46';
            e.currentTarget.style.color = '#fff';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'transparent';
            e.currentTarget.style.color = '#1e7a46';
          }}
        >
          <i className="fa fa-arrow-left"></i> {t.backToArticles}
        </button>
      </div>

      {/* İlgili Makaleler */}
      {relatedArticles.length > 0 && (
        <div style={{ background: isDarkMode ? '#0f0f0f' : '#f8f9fa', padding: '60px 0', marginTop: '20px' }}>
          <div className="container">
            <h3 style={{ fontSize: '32px', marginBottom: '40px', textAlign: 'center', fontWeight: '700', color: isDarkMode ? '#f0f0f0' : '#333' }}>
              {t.relatedArticles}
            </h3>
            <div className="row ap-related-grid">
              {relatedArticles.map((relatedArticle) => {
                const relatedTitle = getLocalizedField(relatedArticle, 'title');
                const relatedDescription = getLocalizedField(relatedArticle, 'description');

                return (
                  <div key={relatedArticle.id} className="col-md-4 col-sm-6 ap-related-col" style={{ marginBottom: '30px' }}>
                    <div
                      onClick={() => handleRelatedArticleClick(relatedArticle.id)}
                      style={{
                        background: isDarkMode ? '#2a2a2a' : '#fff',
                        border: isDarkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid #e0e0e0',
                        borderRadius: '12px',
                        overflow: 'hidden',
                        cursor: 'pointer',
                        transition: 'all 0.3s ease',
                        boxShadow: isDarkMode ? '0 5px 15px rgba(0,0,0,0.3)' : '0 5px 15px rgba(0,0,0,0.08)',
                        textAlign: 'center'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'translateY(-8px)';
                        e.currentTarget.style.boxShadow = '0 10px 25px rgba(0,0,0,0.2)';
                        e.currentTarget.style.borderColor = '#1e7a46';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = isDarkMode ? '0 5px 15px rgba(0,0,0,0.3)' : '0 5px 15px rgba(0,0,0,0.08)';
                        e.currentTarget.style.borderColor = isDarkMode ? 'rgba(255,255,255,0.1)' : '#e0e0e0';
                      }}
                    >
                      {relatedArticle.image && (
                        <img
                          src={relatedArticle.image}
                          alt={relatedTitle}
                          style={{ width: '100%', height: '200px', objectFit: 'cover' }}
                        />
                      )}
                      <div style={{ padding: '20px' }}>
                        <div style={{ color: '#999', fontSize: '14px', marginBottom: '10px' }}>
                          {relatedArticle.date}
                        </div>
                        <h4 style={{ fontSize: '20px', marginBottom: '10px', fontWeight: '600', color: isDarkMode ? '#f0f0f0' : '#333' }}>
                          {relatedTitle}
                        </h4>
                        <p style={{ color: isDarkMode ? '#bbb' : '#666', fontSize: '15px', marginBottom: '15px' }}>
                          {relatedDescription}
                        </p>
                        <span style={{ color: '#1e7a46', fontWeight: '600', fontSize: '14px' }}>
                          {t.readMore} <i className="fa fa-arrow-right"></i>
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ArticlePage;